import {Async, Empty, Loaded, isLoaded} from 'Core/models/Async';
import {AvailableQuoteActions, CopyQuoteAlertState, OpportunityQuote, OpportunitySummaryDto} from 'Quoting/models/DTOs/Opportunity';
import {Proposal, ProposalQuote, ProposalQuoteDetails} from 'Quoting/models/DTOs/Proposal';
import {A360ProductCd} from 'Quoting/models/Quoting';
import {createAction} from 'Shared/utilities/actionCreator';

export enum QuotingActionType {
  GET_OPPORTUNITY_SUMMARY = 'opportunity/GET_OPPORTUNITY_SUMMARY',
  CHANGE_OPPORTUNITY_SUMMARY = 'opportunity/CHANGE_OPPORTUNITY_SUMMARY',
  GET_OPPORTUNITY_QUOTE_LIST = 'opportunity/GET_OPPORTUNITY_QUOTE_LIST',
  CHANGE_OPPORTUNITY_QUOTE_LIST = 'opportunity/CHANGE_OPPORTUNITY_QUOTE_LIST',
  GET_AVAILABLE_QUOTE_ACTIONS = 'opportunity/GET_AVAILABLE_QUOTE_ACTIONS',
  CHANGE_AVAILABLE_QUOTE_ACTIONS = 'opportunity/CHANGE_AVAILABLE_QUOTE_ACTIONS',
  SET_SELECTED_OPPORTUNITY_ID = 'quoting/SET_SELECTED_OPPORTUNITY_ID',
  GET_OPPORUNITY_PROPOSALS = 'opporunity/GET_OPPORUNITY_PROPOSALS',
  UPDATE_OPPORUNITY_PROPOSALS = 'opporunity/UPDATE_OPPORUNITY_PROPOSALS',
  PROPOSAL_FOR_GENERATION = 'opportunity/PROPOSAL_FOR_GENERATION',
  GET_QUOTES_FOR_PROPOSAL = 'opportunity/GET_QUOTES_FOR_PROPOSAL',
  UPDATE_QUOTES_FOR_PROPOSAL = 'opportunity/UPDATE_QUOTES_FOR_PROPOSAL',
  PREPARE_FOR_PROPOSAL = 'opportunity/PREPARE_FOR_PROPOSAL',
  UPDATE_QUOTE_STATUS_AFTER_ACTION = 'opportunity/UPDATE_QUOTE_STATUS_AFTER_ACTION',
  SET_COPY_ALERT_MESSAGE = 'opportunity/SET_COPY_ALERT_MESSAGE',
  CLEAR_COPY_ALERT_MESSAGE = 'opportunity/CLEAR_COPY_ALERT_MESSAGE',
}

export const getOpportunitySummary = (opportunityId: string) =>
  createAction(QuotingActionType.GET_OPPORTUNITY_SUMMARY, {opportunityId});

export const changeOpportunitySummary = (opportunitySummary: Async<OpportunitySummaryDto>) =>
  createAction(QuotingActionType.CHANGE_OPPORTUNITY_SUMMARY, {opportunitySummary});

export const getOpportunityQuoteList = (rootId: string, revisionNo: number) =>
  createAction(QuotingActionType.GET_OPPORTUNITY_QUOTE_LIST, {rootId, revisionNo});

export const getProposalListData = () => createAction(QuotingActionType.GET_OPPORUNITY_PROPOSALS);

export const changeOpportunityQuoteList = (opportunityQuoteList: Async<OpportunityQuote[]>) =>
  createAction(QuotingActionType.CHANGE_OPPORTUNITY_QUOTE_LIST, {opportunityQuoteList});

export const getAvailableQuoteActions = (quotes: {rootId: string; productCd: A360ProductCd}[]) =>
  createAction(QuotingActionType.GET_AVAILABLE_QUOTE_ACTIONS, {quotes});

export const changeAvailableQuoteActions = (availableQuoteActionsMap: Async<Record<string, AvailableQuoteActions[]>>) =>
  createAction(QuotingActionType.CHANGE_AVAILABLE_QUOTE_ACTIONS, {availableQuoteActionsMap});

export const setSelectedOpportunityId = (opportunityId: string) =>
  createAction(QuotingActionType.SET_SELECTED_OPPORTUNITY_ID, opportunityId);

export const updateOpportunityProposals = (opportunityPropsalList: Async<Proposal[]>) =>
  createAction(QuotingActionType.UPDATE_OPPORUNITY_PROPOSALS, opportunityPropsalList);

export const updateProposalToGenerate = (proposal: Async<Proposal[]>) =>
  createAction(QuotingActionType.PROPOSAL_FOR_GENERATION, proposal);

export const getQuotesForProposal = (proposalNumber: string, proposalQuotes: ProposalQuote[]) =>
  createAction(QuotingActionType.GET_QUOTES_FOR_PROPOSAL, {proposalNumber, proposalQuotes});

export const updateQuotesLinkedToProposals = (proposalNumber: string, proposalQuotes: Async<ProposalQuoteDetails[]>) =>
  createAction(QuotingActionType.UPDATE_QUOTES_FOR_PROPOSAL, {proposalNumber, proposalQuotes});

export const updateQuoteStatusAfterAction = (rootId: string, state: string) =>
  createAction(QuotingActionType.UPDATE_QUOTE_STATUS_AFTER_ACTION, {rootId, state});

  export const setCopyQuoteAlertMessage = (message: string, severity: 'success' | 'error' = 'success') => 
  createAction(QuotingActionType.SET_COPY_ALERT_MESSAGE, { message, severity });

export const clearCopyQuoteAlertMessage = () => 
  createAction(QuotingActionType.CLEAR_COPY_ALERT_MESSAGE);  

type QuotingAction =
  | ReturnType<typeof getOpportunitySummary>
  | ReturnType<typeof changeOpportunitySummary>
  | ReturnType<typeof getOpportunityQuoteList>
  | ReturnType<typeof changeOpportunityQuoteList>
  | ReturnType<typeof getAvailableQuoteActions>
  | ReturnType<typeof changeAvailableQuoteActions>
  | ReturnType<typeof setSelectedOpportunityId>
  | ReturnType<typeof updateOpportunityProposals>
  | ReturnType<typeof getProposalListData>
  | ReturnType<typeof updateProposalToGenerate>
  | ReturnType<typeof getQuotesForProposal>
  | ReturnType<typeof updateQuoteStatusAfterAction>
  | ReturnType<typeof updateQuotesLinkedToProposals>
  | ReturnType<typeof setCopyQuoteAlertMessage>
  | ReturnType<typeof clearCopyQuoteAlertMessage>;

type QuotingState = {
  opportunitySummary: Async<OpportunitySummaryDto>;
  selectedOpportunityId: string;
  availableQuoteActionsMap: Async<Record<string, AvailableQuoteActions[]>>;
  proposals: Async<Proposal[]>;
  quotes: Async<OpportunityQuote[]>;
  proposalSelectedForGeneration?: Async<Proposal[]>;
  proposalQuotesMap: Record<string, Async<ProposalQuoteDetails[]>>;
  copyQuoteAlert: CopyQuoteAlertState | null;
};

const DEFAULT_QUOTING_STATE: QuotingState = {
  selectedOpportunityId: '',
  availableQuoteActionsMap: new Empty(),
  proposals: new Empty(),
  quotes: new Empty(),
  opportunitySummary: new Empty(),
  proposalSelectedForGeneration: new Empty(),
  proposalQuotesMap: {},
  copyQuoteAlert: null,
};

export default (state: QuotingState = DEFAULT_QUOTING_STATE, action: QuotingAction): QuotingState => {
  switch (action.type) {
    case QuotingActionType.SET_SELECTED_OPPORTUNITY_ID:
      return {
        ...state,
        selectedOpportunityId: action.payload,
      };

    case QuotingActionType.UPDATE_OPPORUNITY_PROPOSALS:
      return {
        ...state,
        proposals: action.payload,
      };

    case QuotingActionType.PROPOSAL_FOR_GENERATION:
      return {
        ...state,
        proposalSelectedForGeneration: action.payload,
      };
    
      case QuotingActionType.SET_COPY_ALERT_MESSAGE:
        return {
          ...state,
          copyQuoteAlert: {
            message: action.payload.message,
            severity: action.payload.severity,
          },
        };
  
      case QuotingActionType.CLEAR_COPY_ALERT_MESSAGE:
        return {
          ...state,
          copyQuoteAlert: null,
        };  

    case QuotingActionType.CHANGE_OPPORTUNITY_SUMMARY:
      return {
        ...state,
        opportunitySummary: action.payload.opportunitySummary,
      };

    case QuotingActionType.CHANGE_OPPORTUNITY_QUOTE_LIST:
      return {
        ...state,
        quotes: action.payload.opportunityQuoteList,
      };

    case QuotingActionType.CHANGE_AVAILABLE_QUOTE_ACTIONS:
      return {
        ...state,
        availableQuoteActionsMap: action.payload.availableQuoteActionsMap,
      };

    case QuotingActionType.UPDATE_QUOTES_FOR_PROPOSAL:
      return {
        ...state,
        proposalQuotesMap: {
          ...state.proposalQuotesMap,
          [action.payload.proposalNumber]: action.payload.proposalQuotes,
        },
      };

    case QuotingActionType.UPDATE_QUOTE_STATUS_AFTER_ACTION: {
      if (isLoaded(state.quotes)) {
        const quoteIndex = state.quotes.Data?.findIndex(q => q?.entity?._key?.rootId === action.payload.rootId);
        if (quoteIndex === -1) return state;

        const updatedQuotes = [
          ...state.quotes.Data.slice(0, quoteIndex),
          {
            ...state.quotes.Data[quoteIndex],
            entity: {...state.quotes.Data[quoteIndex].entity, state: action.payload.state},
            ...state.quotes.Data.slice(quoteIndex, 1),
          },
        ];
        return {
          ...state,
          quotes: new Loaded(updatedQuotes),
        };
      }
      return state;
    }
    default:
      return state;
  }
};
